// utils/salePosting.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- SHARED, production POST /sales body types +
// the exact fee-resolution builder that SaleEntry itself calls. Extracted so
// the request body is strictly typed (Condition 4b / Finding 1) and the actual
// posting logic is unit-testable (Finding 3) rather than duplicated in a test.

import { buildFeeIntent, FeePayloadFields } from "./feeIntent";
import { parseFeeOverride } from "./feeOverride";
import { FeeMode } from "./saleFeeHydration";

export type SaleLinePostBody = {
  product_id: number | null;
  detail?: string;
  qty: number;
  unit_price: number;
  discount: number;
  item_kind: string;
  preorder: boolean;
};

// The business payload (everything except idempotency_key). Fee fields come
// ONLY from FeePayloadFields (configured carries no fee; explicit carries a
// numeric fee; no-payment carries neither), so a stray fee can never attach.
export type SalePostBody = {
  date: string;
  lines: SaleLinePostBody[];
  payment_method_id: number;
  on_account: boolean;
  memo: string;
  send_email_now: boolean;
  customer_id?: number;
  due_date?: string | null;
  amount_paid?: number;
  payment_account_id?: number;
} & FeePayloadFields;

// The ACTUAL object posted to apiPost -- the business payload plus the key.
export type SalePostRequest = SalePostBody & { idempotency_key: string };

export type SaleFeeResolution =
  | { ok: true; hasPositiveOfflinePayment: boolean; feeFields: FeePayloadFields }
  | { ok: false; reason: "invalid-fee" };

/**
 * Resolve the offline-payment fee fields SaleEntry attaches to POST /sales.
 * This is the SAME function the page calls (not a test duplicate).
 *
 * - no positive offline payment (on-account, Stripe-online, or amount <= 0)
 *   -> configured/explicit are irrelevant; attach NEITHER field;
 * - cash -> explicit 0;
 * - explicit intent (user-entered Fee) -> strictly validated numeric fee;
 *   an invalid non-blank fee -> { ok:false } so the caller blocks before apiPost;
 *   a blank fee under explicit mode falls back to configured;
 * - otherwise (automatic noncash) -> configured (backend authoritative, no fee).
 */
export function resolveSaleFeeFields(input: {
  isOnAccount: boolean;
  wantsStripeOnline: boolean;
  amountPaid: number;
  isCash: boolean;
  feeMode: FeeMode;
  feeRaw: string;
}): SaleFeeResolution {
  const hasPositiveOfflinePayment =
    !input.isOnAccount && !input.wantsStripeOnline && input.amountPaid > 0;
  const explicit =
    hasPositiveOfflinePayment && !input.isCash && input.feeMode === "explicit";
  let feeValue: number | null = null;
  if (explicit) {
    const parsed = parseFeeOverride(input.feeRaw); // Number(), not parseFloat
    if (!parsed.ok && parsed.reason === "invalid") {
      return { ok: false, reason: "invalid-fee" };
    }
    feeValue = parsed.ok ? parsed.value : null;
  }
  const intent = buildFeeIntent({
    hasPositiveOfflinePayment,
    isCash: input.isCash,
    explicit: explicit && feeValue !== null,
    feeValue,
  });
  return { ok: true, hasPositiveOfflinePayment, feeFields: intent ?? {} };
}
